import { ft } from '../core/units.js';
import { parseTime } from './clock.js';
import { normalMinutes } from './beoGen.js';
import { key } from './setup.js';
import { JOBS, JOBS_BY_ID } from '../data/jobs.js';

/**
 * The three daily jobs (`data/jobs.js`): a card on the board each, asked for
 * by somebody, and set through everything a room already is. What a job adds
 * to a room is the state it starts in and one more thing to do:
 *
 *   flip      the lunch's room is standing when you take it
 *   refresh   the room is set, then undone in places, with glasses on the floor
 *             to carry out to the kitchen pass
 *   coffee    an urn waiting at the kitchen pass, to be put down by a station
 *
 * `part` is that one more thing, as a share done, and it is a quarter of the
 * grade (`main.js`). The rest is the room, graded like any room.
 *
 * Pure but for the entities it puts down and the room it loads.
 */
export const JOB_SHARE = 0.25;

/** Feet within which the urn counts as at a station, and glasses as cleared to the pass. */
const NEAR_STATION = 8;
const NEAR_PASS = 9;

function clockText(minutes) {
  const wrapped = ((Math.round(minutes) % 1440) + 1440) % 1440;
  return `${String(Math.floor(wrapped / 60)).padStart(2, '0')}:${String(wrapped % 60).padStart(2, '0')}`;
}

/** The card for a job on a day, off the shift it falls in. `giver` is whoever asks for it. */
export function cardFor(row, day, shift, giver = null) {
  const start = parseTime(shift.start);
  let end = parseTime(shift.end);
  if (end <= start) end += 24 * 60;
  // Due when its row says, or later if a normal player could not set it by
  // then from the moment it posts (`normalMinutes` in `beoGen.js`), and never
  // past a quarter of an hour before the shift ends.
  const due = Math.min(Math.max(row.posts.due, row.posts.at + normalMinutes(row.estimate)), end - start - 15);
  return {
    id: `job-${row.id}-d${day}`,
    code: `${row.code} ${100 + day}`,
    title: row.title,
    client: giver ? `Asked for by ${giver}` : 'Posted by the section',
    owner: row.owner,
    type: row.id,
    room: 'ballroom',
    job: row.id,
    day,
    covers: 0,
    ghosts: false,
    setup: { ...row.setup },
    shortfalls: [],
    award: { ...row.award },
    estimate: row.estimate,
    note: row.note,
    postedAt: clockText(start + row.posts.at),
    setupBy: clockText(start + due),
    doors: clockText(start + due + 30),
  };
}

/** Every anchor a layout covers, as keys. */
function keysOf(layout) {
  const out = [];
  for (let c = layout.col; c < layout.col + layout.cols; c += 1) {
    for (let r = layout.row; r < layout.row + layout.rows; r += 1) out.push(key(c, r));
  }
  return out;
}

/*
 * `carried()` is how many of the job's things are in your hands now (they are
 * not entities while they are), `inRoom(point)` whether a point is in the
 * ballroom, and `setup()` the ballroom's setup.
 */
export function createJobs({ entities, setup, inRoom, carried = () => 0 }) {
  /** The job taken, and what it put down. */
  let current = null;

  /*
   * What the job put down, by its claim: picking a thing up and putting it
   * down again makes a new entity, and the claim is what goes with it
   * (`main.js`'s pickup, `throwing.drop`). A claim that is not a room's id is
   * nobody's shortfall (`sim/claims.js` only counts rounds, chairs and linen).
   */
  const tag = (id) => `job:${id}`;
  const mine = () => (current ? entities.all.filter((e) => e.claim === tag(current.id)) : []);

  return {
    rows: JOBS,

    /** Puts the room and the things in the state the job starts in. Once, on accept. */
    begin(beo) {
      const row = JOBS_BY_ID[beo.job];
      if (!row) return null;
      current = { id: row.id, beo: beo.id };
      const room = setup();
      const before = row.before;
      const tables = [];
      if (before.layout === 'setup' || before.layout) {
        const layout = before.layout === 'setup' ? row.setup.layout : before.layout;
        const linen = before.linen ?? row.setup.linen;
        const chairs = before.chairs ?? row.setup.chairsPerTable;
        const keys = keysOf(layout);
        keys.forEach((k, i) => {
          const anchor = room.anchorAt(k);
          if (!anchor) return;
          const stripped = (before.stripped ?? 0) > 0 && i >= keys.length - before.stripped;
          tables.push({
            key: k,
            col: anchor.col,
            row: anchor.row,
            kind: 'round',
            variant: null,
            cover: stripped ? null : { kind: 'linen', variant: linen },
            seats: Math.max(0, chairs - (i === 0 ? before.missing ?? 0 : 0)),
            seatKind: chairs > 0 ? 'chair' : null,
          });
        });
      }
      room.load({ tables });
      // Glasses down round the tables, a little off each round.
      if (before.litter) {
        const keys = keysOf(row.setup.layout);
        for (let i = 0; i < before.litter.count; i += 1) {
          const anchor = room.anchorAt(keys[i % keys.length]);
          const angle = (i * 2.4) % (Math.PI * 2);
          entities.spawn({
            kind: before.litter.kind,
            position: { x: anchor.x + Math.cos(angle) * ft(5.2), y: 0, z: anchor.z + Math.sin(angle) * ft(5.2) },
            claim: tag(row.id),
          });
        }
      }
      if (before.bring) {
        const [x, z] = before.bring.from;
        for (let i = 0; i < before.bring.count; i += 1) {
          entities.spawn({ kind: before.bring.kind, position: { x: ft(x + i), y: 0, z: ft(z) }, claim: tag(row.id) });
        }
      }
      return current;
    },

    get current() {
      return current ? { ...current } : null;
    },

    /**
     * The job's own part, as a share done: glasses out of the ballroom and at
     * the pass, the urn by a station. Null for a job with nothing of its own.
     */
    part() {
      if (!current) return null;
      const row = JOBS_BY_ID[current.id];
      if (row.before.litter) {
        // Out of the ballroom counts; still in it, or in your hands, does not.
        const total = row.before.litter.count;
        const inside = mine().filter((e) => e.kind === row.before.litter.kind && inRoom(e.position)).length;
        const held = carried();
        return Math.max(0, total - inside - held) / total;
      }
      if (row.before.bring) {
        const things = mine().filter((e) => e.kind === row.before.bring.kind);
        const room = setup();
        const stations = keysOf(row.setup.layout).map((k) => room.anchorAt(k)).filter(Boolean);
        const there = things.filter((e) => e.state === 'world' && stations.some((a) => Math.hypot(e.position.x - a.x, e.position.z - a.z) < ft(NEAR_STATION))).length;
        return there / row.before.bring.count;
      }
      return null;
    },

    /**
     * Where the job's own thing wants you, after the room's: the nearest glass
     * still in the room (or the pass, with one in your hands), the urn (or the
     * station, with it in your hands). Null when there is nothing of its own left.
     */
    next(holding, from) {
      if (!current) return null;
      const row = JOBS_BY_ID[current.id];
      const nearest = (list) => list.map((e) => ({ e, d: Math.hypot(e.position.x - from.x, e.position.z - from.z) })).sort((a, b) => a.d - b.d)[0]?.e ?? null;
      if (row.before.litter) {
        if (holding.kind === row.before.litter.kind && holding.count > 0) {
          const [x, z] = row.clearTo;
          return { point: { x: ft(x), z: ft(z) }, say: `Carry the glasses out to the bus tub at the kitchen pass, and put them down there ([G])`, drop: true };
        }
        const left = mine().filter((e) => e.kind === row.before.litter.kind && inRoom(e.position));
        const g = nearest(left);
        return g ? { point: { x: g.position.x, z: g.position.z }, say: `${left.length === 1 ? 'A glass is' : `${left.length} glasses are`} on the floor: pick them up ([E])` } : null;
      }
      if (row.before.bring) {
        const room = setup();
        const stations = keysOf(row.setup.layout).map((k) => room.anchorAt(k)).filter(Boolean);
        if (holding.kind === row.before.bring.kind && holding.count > 0) {
          const s = stations[0];
          return s ? { point: { x: s.x, z: s.z }, say: 'Put the urn down by a station ([G])', drop: true } : null;
        }
        const waiting = mine().filter((e) => e.kind === row.before.bring.kind && !stations.some((a) => Math.hypot(e.position.x - a.x, e.position.z - a.z) < ft(NEAR_STATION)));
        const u = nearest(waiting);
        return u ? { point: { x: u.position.x, z: u.position.z }, say: 'Fetch the coffee urn from the kitchen pass ([E] to pick it up)' } : null;
      }
      return null;
    },

    /** Done with: the job's things stay where they were left, and are nobody's. */
    finish() {
      for (const e of mine()) e.claim = null;
      current = null;
    },

    clear() {
      current = null;
    },
  };
}
